import React from "react";
import { FormattedMessage } from "react-intl";
import { withRouter } from "react-router";
import "../commons.scss";
import { TrackedLink } from "./TrackedComponents";
import { ImageWithFallbacks } from "./ImageWithFallbacks";
import { getUrls } from "../cart-page/ArticleBadgeImage";
import { withIntlRoot } from "../../IntlRootMixin";
import { connectStore } from "../../utils/RefluxConnect";
import { SideCaddyStore } from "../../stores";
import MoneyAmount from "../MoneyAmount";
import { SideCaddyActions } from "../../actions/SideCaddyActions";
import { ArticleBadgeControls } from "../cart-page/ArticleBadgeControls";
import { ArticleLink } from "../cart-page/ArticleLink";
import { processConfig } from "../../stores/PriceFormatUtil";
import { withReactAdapter } from "../../module-federation/withReactAdapter";

import type { SideCaddyStoreState } from "../../stores/SideCaddyStore";
import type { CommonCartItem } from "../../model/CommonCartTypes";
import type { PriceConfiguration } from "../../stores/BaseTypes";
import { CartDeliveryDatePicker } from "../date-picker/DatePicker";

export type SideCaddyProps = SideCaddyStoreState & {
	router: Record<string, any>;
} & typeof SideCaddyActions;
export type CaddyItemProps = CommonCartItem & {
	maxQuantityNormal: number;
	maxQuantityWeight: number;
	priceConfiguration: PriceConfiguration;
	storeName: string | null | undefined;
};

export const CaddyItemWeight = (props: CaddyItemProps) => {
	return props.weightPerPiece ? (
		<FormattedMessage
			id={"ORC_WEIGHT_PER_PIECES"}
			values={{
				weightPerPiece: props.weightPerPiece.value,
				unitOfMeasurement: props.weightPerPiece.unitOfMeasurement.toLowerCase(),
			}}
			data-selenium={"article-badge-weight-per-piece-label"}
		/>
	) : (
		(props.packaging && props.packaging.description) || null
	);
};
export const CaddyItem = (props: CaddyItemProps) => {
	const configuredPrice = processConfig(props.prices, props.priceConfiguration);
	const primaryPrice = configuredPrice.valid ? configuredPrice.positionPrices.primary.amount : null;
	const control = (
		<ArticleBadgeControls
			article={props}
			configuredPrice={configuredPrice as any}
			commentEnabled={false}
			showComment={false}
			onSetAmount={SideCaddyActions.setItemQuantity}
			onDeleteItem={SideCaddyActions.deleteItem}
			maxQuantityNormal={props.maxQuantityNormal}
			maxQuantityWeight={props.maxQuantityWeight}
			isSideCaddy
			storeName={props.storeName}
		/>
	);
	return (
		<div className="mfcss_side-caddy-item border-bottom">
			<div className="mfcss_side-caddy-item--image">
				<ImageWithFallbacks
					urls={getUrls({
						bundleId: props.bundleId,
						imageSources: props.imageSources,
					})}
					alt={props.description}
				/>
			</div>
			<div className="mfcss_side-caddy-item--content">
				<div className="mfcss_side-caddy-item--title">
					<ArticleLink article={props} />
				</div>
				<div className="mfcss_side-caddy-item--info">
					<label className="label-pack label-rectangular">
						<CaddyItemWeight {...props} />
					</label>
				</div>
				<div className="mfcss_side-caddy-item--price">
					<span className="price primary">
						<MoneyAmount amount={primaryPrice} />
					</span>
				</div>
				<div className="mfcss_side-caddy-item--control">{control}</div>
			</div>
		</div>
	);
};
export const EmptyCaddyHeader = () => (
	<div>
		<div className="oc-well oc-padding-sm no-bottom-margin oc-border-bottom">
			<div className="flex-set">
				<div className="flexed-item--auto">
					<h4 className="no-bottom-margin">
						<FormattedMessage id="ORC_SIDE_CADDY_EMPTY" />
					</h4>
				</div>
			</div>
		</div>
	</div>
);
export const ShowAllArticlesFooter = ({
	numArticles,
	router,
	trackGoToCart,
}: {
	numArticles: number;
	router: Record<string, any>;
	trackGoToCart: () => void;
}) => (
	<div className="oc-well mfcss_side-caddy-footer">
		<TrackedLink
			tags={{
				source: "SideCaddy.showAllArticles",
			}}
			onClick={() => {
				goToCart(router, trackGoToCart);
			}}
		>
			<FormattedMessage
				id="ORC_SIDE_CADDY_FOOTER_SHOW_ALL"
				values={{
					number: numArticles,
				}}
			/>
		</TrackedLink>
	</div>
);
export const ShowAllOrdersFooter = ({ router }: { router: Record<string, any> }) => (
	<div className="oc-well mfcss_side-caddy-footer">
		<TrackedLink
			tags={{
				source: "SideCaddy.restock",
			}}
			onClick={() => {
				router.push("/portal/my-orders/all");
			}}
		>
			<FormattedMessage id="ORC_SHOPPINGCARTPAGE_RESTOCK_LINK_ALL_ORDERS" />
		</TrackedLink>
	</div>
);

function getItems(itemsObject: Record<string, any>): Array<CommonCartItem> {
	const items: any = [];

	for (const key in itemsObject) {
		if (itemsObject[key]) items.push(itemsObject[key]);
	}

	return items.sort((a, b) => b.addedTimestamp - a.addedTimestamp);
}

function goToCart(router: Record<string, any>, tracking: () => void) {
	tracking();
	router.push("/cart");
}

export const DisconnectedSideCaddy = ({
	cart,
	router,
	maxQuantityNormal,
	maxQuantityWeight,
	priceConfiguration,
	storeName,
	trackGoToCart,
}: SideCaddyProps) => {
	if (!cart) return null;
	const items = getItems(cart.items);
	const numArticles = items.length;
	const sideCaddyClassName = "mfcss_side-caddy mfcss_side-caddy-early";

	if (!numArticles) {
		return (
			<div className={sideCaddyClassName + " flex--content-between flex--column"}>
				<div>
					<CartDeliveryDatePicker variant="readonly" />
					<EmptyCaddyHeader />
					<div className="oc-well oc-padding-sm">
						<FormattedMessage id="ORC_EMPTY_SIDE_CADDY_MESSAGE" />
					</div>
				</div>
			</div>
		);
	}

	const caddyItems = items.map((it) => {
		return (
			<CaddyItem
				key={it.bundleId}
				{...it}
				maxQuantityNormal={maxQuantityNormal}
				maxQuantityWeight={maxQuantityWeight}
				storeName={storeName}
				priceConfiguration={priceConfiguration}
			/>
		);
	});
	return (
		<div className={sideCaddyClassName + " flex--content-between flex--column"}>
			<CartDeliveryDatePicker variant="readonly" />
			<div>
				<div className="oc-well oc-padding-sm no-top-padding">{caddyItems}</div>
			</div>
			<div>
				<ShowAllArticlesFooter numArticles={numArticles} router={router} trackGoToCart={trackGoToCart} />
			</div>
		</div>
	);
};

function withBodyTag(WrappedComponent: React.ComponentType<SideCaddyProps>): React.ComponentType<SideCaddyProps> {
	return class Wrapper extends React.Component<SideCaddyProps> {
		componentDidMount() {
			this.setBodyTag();
		}

		setBodyTag() {
			if (this.props.cart && this.props.isEnabled) {
				const body = document.getElementsByTagName("body")[0];
				body.classList.add("with-side-caddy");
				body.classList.add("with-side-caddy-early");
			}
		}

		componentDidUpdate() {
			this.setBodyTag();
		}

		componentWillUnmount() {
			const body = document.getElementsByTagName("body")[0];
			body.classList.remove("with-side-caddy");
			body.classList.remove("with-side-caddy-early");
		}

		render() {
			return this.props.isEnabled ? <WrappedComponent {...this.props} /> : null;
		}
	};
}

export const SideCaddy = withIntlRoot(
	connectStore(withBodyTag(withRouter(DisconnectedSideCaddy)), SideCaddyStore, SideCaddyActions),
);
export default withReactAdapter(SideCaddy);
